import React, { useEffect, useState } from "react";
import { Spin, Select } from "antd";
import useHttp from "../../../hooks/use-http";

const { Option } = Select;

const WeatherForecast = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState(null);
  const [selectedDay, setSelectedDay] = useState("today");
  const [permissionDenied, setPermissionDenied] = useState(false);

  const { sendRequest: fetchWeather } = useHttp();

  const callWeatherAPI = (lat, lon) => {
    const url = `/api/weatherForecastProcurement/?lat=${lat}&lon=${lon}`;
    console.log("[WeatherForecast] calling:", url);

    fetchWeather({ url }, (res) => {
      console.log("[WeatherForecast] response:", res);
      setWeather(res || null);
      setLoading(false);
      setPermissionDenied(false);
    });
  };

  const requestLocationAndFetch = () => {
    setLoading(true);
    setPermissionDenied(false);

    if (!navigator.geolocation) {
      console.error("[WeatherForecast] Geolocation not supported");
      setLoading(false);
      setPermissionDenied(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        console.log("[WeatherForecast] GPS success", pos);
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        callWeatherAPI(lat, lon);
      },
      (err) => {
        console.error("[WeatherForecast] GPS error", err);
        setPermissionDenied(true);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    requestLocationAndFetch();
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-2xl shadow-md"
        style={{
          height: "200px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin />
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <div
        className="rounded-2xl shadow-md"
        style={{
          background: "#f3e8d3",
          height: "200px",
          width: "100%",
          padding: "10px",
          textAlign: "center",
        }}
      >
        <h3 style={{ color: "rgba(215, 48, 48, 1)", margin: 0, fontWeight: 700 }}>
          Location permission required
        </h3>
        <p style={{ color: "#700", marginTop: 8 }}>
          Please allow location access to fetch weather.
        </p>
        <button
          onClick={requestLocationAndFetch}
          style={{
            padding: "8px 14px",
            background: "#0b63d6",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!weather) {
    return (
      <div
        className="rounded-2xl shadow-md"
        style={{
          height: "200px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div>Weather data unavailable</div>
      </div>
    );
  }

  const today = weather;
  const tomorrow = weather.tomorrow;
  const data = selectedDay === "today" ? today : tomorrow;

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: 20,
          background: "#eef7ff",
          borderRadius: 12,
          boxSizing: "border-box",
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, color: "#12345b", fontSize: 24, fontWeight: 700 }}>
            Weather Forecast
          </h2>

          <Select value={selectedDay} style={{ width: 160 }} onChange={(v) => setSelectedDay(v)}>
            <Option value="today">Today</Option>
            <Option value="tomorrow">Tomorrow</Option>
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 48, flexWrap: "wrap" }}>
          <div style={{ minWidth: 160, textAlign: "center" }}>
            <img
              src={data.icon}
              alt={data.condition}
              style={{ width: 120, height: 120, objectFit: "contain", display: "block", margin: "0 auto" }}
            />
            <div style={{ marginTop: 10, fontSize: 20, color: "#23476d", fontWeight: 600 }}>{data.condition}</div>
          </div>

          <div style={{ minWidth: 260, maxWidth: 520 }}>
            <div style={{ fontSize: 64, fontWeight: 800, color: "#12345b", lineHeight: 1 }}>
              {data.maxTemp}°
            </div>
            <div style={{ fontSize: 18, marginTop: -6, color: "#12345b" }}>Min: {data.minTemp}°</div>

            <div style={{ marginTop: 16, fontSize: 16 }}>
              <strong>Rain Chance:</strong> {data.rainChance}%
            </div>
            <div style={{ marginTop: 8, fontSize: 16 }}>
              <strong>Wind:</strong> {weather.windSpeed} km/h {weather.windDirection}
            </div>
            <div style={{ marginTop: 8, fontSize: 16 }}>
              <strong>Date:</strong> {data.date}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: 200,
        width: "100%",
        padding: 18,
        position: "relative",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <h3 style={{ textAlign: "center", margin: 0, marginBottom: 10, color: "#3d5c26", fontWeight: 700 }}>Weather Forecast</h3>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", height: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 50, }}>
          <img src={today.icon} alt={today.condition} style={{ width: 56, height: 56, objectFit: "contain" }} />
          <div style={{ marginTop: 6, fontSize: 14, color: "#3d5c26" }}>{today.condition}</div>
        </div>

        <div style={{ textAlign: "left", marginBottom: 50, }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#3d5c26" }}>{today.maxTemp}°</div>
          <div style={{ fontSize: 14, color: "#3d5c26", marginTop: 4 }}>Min {today.minTemp}°</div>
        </div>

        <div style={{ textAlign: "left", fontSize: 13, marginBottom: 50, color: "#3d5c26" }}>
          <div>Rain {today.rainChance}%</div>
          <div style={{ marginTop: 6 }}>Wind {today.windSpeed} km/h</div>
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 10,
          right: 16,
          cursor: "pointer",
          fontWeight: 600,
          color: "#3d5c26",
        }}
        onClick={openMaximize}
      >
        More...
      </div>
    </div>
  );
};

export default WeatherForecast;
